// Home page thumbnails, stored the way sumatrapdf-ng stores them so both show
// the same ones: settings/sumatrapdfcache/<md5 of "/uploads/<name>">.png,
// the whole first page scaled to fit 150x212 (ThumbnailsType "full").
// See GetThumbnailPathTemp() in src/shared/FileThumbnailsCommon.cpp.
//
// sumatrapdf-ng re-creates a thumbnail older than its document, or one that
// doesn't fit the frame of its ThumbnailsType, and we do the same.

import { NG_UPLOADS_DIR, ngFileModified, withOpfsLock } from "./ngstore";
import { writeOpfsFile } from "./opfswrite";

export const NG_THUMB_WIDTH = 150;
export const NG_THUMB_HEIGHT = 212;

const SETTINGS_DIR = "settings";
const CACHE_DIR = "sumatrapdfcache";

export function ngThumbnailFileName(name: string): string {
  return `${md5Hex(new TextEncoder().encode(`${NG_UPLOADS_DIR}/${name}`))}.png`;
}

async function cacheDir(create: boolean): Promise<FileSystemDirectoryHandle | null> {
  if (!navigator.storage?.getDirectory) {
    return null;
  }
  try {
    const root = await navigator.storage.getDirectory();
    const settings = await root.getDirectoryHandle(SETTINGS_DIR, { create });
    return await settings.getDirectoryHandle(CACHE_DIR, { create });
  } catch {
    return null;
  }
}

// null if there's no thumbnail or it's older than the document (modified at docTime)
export async function readNgThumbnail(name: string, docTime: number): Promise<File | null> {
  const dir = await cacheDir(false);
  if (!dir) {
    return null;
  }
  try {
    const file = await (await dir.getFileHandle(ngThumbnailFileName(name))).getFile();
    if (file.lastModified < docTime || !fitsFrame(await pngSize(file))) {
      return null;
    }
    return file;
  } catch {
    return null;
  }
}

export async function hasNgThumbnail(name: string): Promise<boolean> {
  return (await readNgThumbnail(name, await ngFileModified(name))) !== null;
}

// renderedPage: the first page, rendered at any size
export async function writeNgThumbnail(name: string, renderedPage: HTMLCanvasElement): Promise<void> {
  const scale = Math.min(NG_THUMB_WIDTH / renderedPage.width, NG_THUMB_HEIGHT / renderedPage.height, 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(renderedPage.width * scale));
  canvas.height = Math.max(1, Math.round(renderedPage.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(renderedPage, 0, 0, canvas.width, canvas.height);
  const png = await canvasToPng(canvas);
  await withOpfsLock(async () => {
    const dir = await cacheDir(true);
    if (!dir) {
      return;
    }
    const handle = await dir.getFileHandle(ngThumbnailFileName(name), { create: true });
    await writeOpfsFile(handle, png);
  });
}

export function deleteNgThumbnail(name: string): Promise<void> {
  return withOpfsLock(async () => {
    const dir = await cacheDir(false);
    try {
      await dir?.removeEntry(ngThumbnailFileName(name));
    } catch {
      // there was none
    }
  });
}

// the thumbnail follows a renamed document, as it's named after its path
export function renameNgThumbnail(name: string, newName: string): Promise<void> {
  return withOpfsLock(async () => {
    const dir = await cacheDir(false);
    if (!dir) {
      return;
    }
    let data: ArrayBuffer;
    try {
      data = await (await (await dir.getFileHandle(ngThumbnailFileName(name))).getFile()).arrayBuffer();
    } catch {
      return;
    }
    const handle = await dir.getFileHandle(ngThumbnailFileName(newName), { create: true });
    await writeOpfsFile(handle, data);
    await dir.removeEntry(ngThumbnailFileName(name));
  });
}

function fitsFrame(size: { width: number; height: number } | null): boolean {
  return !!size && size.width <= NG_THUMB_WIDTH && size.height <= NG_THUMB_HEIGHT;
}

// from the IHDR chunk, like PngSizeFromData() in sumatrapdf
async function pngSize(file: File): Promise<{ width: number; height: number } | null> {
  const b = new Uint8Array(await file.slice(0, 24).arrayBuffer());
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (b.length < 24 || sig.some((v, i) => b[i] !== v)) {
    return null;
  }
  const view = new DataView(b.buffer);
  return { width: view.getUint32(16), height: view.getUint32(20) };
}

function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("failed to create thumbnail PNG"))), "image/png");
  });
}

// WebCrypto has no MD5. RFC 1321.
const S = [7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21];
const K = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32) >>> 0);

export function md5Hex(input: Uint8Array): string {
  const bitLen = input.length * 8;
  const padded = new Uint8Array((((input.length + 8) >> 6) + 1) << 6);
  padded.set(input);
  padded[input.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, bitLen >>> 0, true);
  view.setUint32(padded.length - 4, Math.floor(bitLen / 2 ** 32), true);

  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;
  const m = new Uint32Array(16);
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) {
      m[i] = view.getUint32(off + i * 4, true);
    }
    let a = a0;
    let b = b0;
    let c = c0;
    let d = d0;
    for (let i = 0; i < 64; i++) {
      let f: number;
      let g: number;
      if (i < 16) {
        f = (b & c) | (~b & d);
        g = i;
      } else if (i < 32) {
        f = (d & b) | (~d & c);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = (7 * i) % 16;
      }
      const t = d;
      d = c;
      c = b;
      const x = (a + f + K[i] + m[g]) >>> 0;
      b = (b + ((x << S[i]) | (x >>> (32 - S[i])))) >>> 0;
      a = t;
    }
    a0 = (a0 + a) >>> 0;
    b0 = (b0 + b) >>> 0;
    c0 = (c0 + c) >>> 0;
    d0 = (d0 + d) >>> 0;
  }
  const out = new DataView(new ArrayBuffer(16));
  [a0, b0, c0, d0].forEach((v, i) => out.setUint32(i * 4, v, true));
  return [...new Uint8Array(out.buffer)].map((x) => x.toString(16).padStart(2, "0")).join("");
}
